import React, { useEffect, useState } from "react";
import {
  ShoppingBagIcon,
  XMarkIcon,
  MinusIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { TrashIcon } from "@heroicons/react/24/solid";
import { Tooltip } from "react-tooltip";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
  SheetDescription,
} from "../shadcn_components/ui/sheet.tsx";
import { Button } from "../shadcn_components/ui/button.tsx";
import { ScrollArea } from "../shadcn_components/ui/scroll-area.tsx";
import { generateBrandColors, checkColorAccessibility } from "./Util/HSB";
import { BrandColorProvider } from "./Util/BrandColorProvider";
import { setContrast } from "./Util/ColorSwap.js";

const freqMap = {
  MONTHLY: " /month",
  YEARLY: " /year",
  ONE_TIME: "",
  WEEKLY: " /week",
  DAILY: " /day",
  DAY: " /day",
  MONTH: " /month",
  QUARTERLY: " /quarter",
  SEMI_ANNUAL: " /semi-annual",
  ANNUAL: " /year",
  ANNUALLY: " /year",
  ANNUALY: " /year",
};

// First, refine our animation styles
const cartItemStylesOld = {
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  overflow: "hidden",
  opacity: 1,
  maxHeight: "200px",
  transform: "translateX(0)",
  marginTop: "8px",
  marginBottom: "8px",
  borderRadius: "6px",
  border: "1px solid rgba(30, 30, 30, 0.05)",
  background: "#FCFEFF",
  boxShadow: "0px 1px 1px 0px rgba(0, 0, 0, 0.25)",
};

const removingItemStyles = {
  opacity: 0,
  maxHeight: 0,
  transform: "translateX(-4px)", // Subtle slide effect
  marginTop: 0,
  marginBottom: 0,
  padding: 0,
};

const AddToCartSheet = () => {
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [removingItems, setRemovingItems] = useState(new Set());
  const [isMobile, setIsMobile] = useState(false);
  const [sourceWebsite, setSourceWebsite] = useState("");
  const [checkoutPayload, setCheckoutPayload] = useState({
    cartItems: [],
    currency: "USD",
    totalAmount: 0,
  });
  const [elementID, setElementID] = useState("");
  const [BUTTON_ACTIVE_COLOR, setBUTTON_ACTIVE_COLOR] = useState("#2A6EA5");
  const [brandColors, setBrandColors] = useState([]);
  const [showGradient, setShowGradient] = useState(true);

  // Initialize cart and sourceWebsite
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const website = urlParams.get("sourceWebsite");
    setSourceWebsite(website);
    const elementID = urlParams.get("elementID");
    setElementID(elementID);
    const color = urlParams.get("color");
    setBUTTON_ACTIVE_COLOR(color);
    setBrandColors(generateBrandColors(urlParams.get("color")));

    // Get initial cart state
    window.top.postMessage(
      {
        action: "GET_CART",
        entity: "CHARITYSTACK",
        sender: "ADD_TO_CART_SHEET",
        id: elementID,
      },
      "*",
    );
  }, []);

  // Single useEffect for message handling
  useEffect(() => {

    const handleMessage = async (event) => {
      const data = event.data;

      // More descriptive logging with structured data
      console.log("[Cart Data Received]", {
        action: data.action,
        cartData: data.data,
        timestamp: new Date().toISOString(),
      });

      // check that data.data is a json string
      if (
        typeof data.data === "string" &&
        data.data !== "null" &&
        data.data !== "undefined"
      ) {
        try {
          const parsedData = JSON.parse(data.data);
          let cartItems = Array.isArray(parsedData.cartItems)
            ? parsedData.cartItems
            : [];

          if (cartItems.length) {
            const cartItemData = await Promise.all(
              cartItems.map(async (item) => {
                const response = await fetch(
                  `${process.env.REACT_APP_ELEMENTS_BACKEND_URL}/elements-config?elementID=${item.formID}&baseData=true`,
                );
                const data = await response.json();
                return {
                  ...item,
                  fundraiser: data.formName || item.fundraiser,
                  cartItemKey:
                    item.cartItemKey.split("_").slice(0, -1).join("_") +
                    `_${data.formName || item.fundraiser}`,
                };
              }),
            );
            cartItems = cartItemData;
          }
          console.log("[Parsed Cart Items]", cartItems);
          setCheckoutPayload({
            ...parsedData,
            cartItems: cartItems,
          });
          data.data = {
            ...parsedData,
            cartItems: cartItems,
          };
          window.top.postMessage(
            {
              action: "UPDATE_FORM_NAME",
              entity: "CHARITYSTACK",
              sender: "ADD_TO_CART_SHEET",
              id: elementID,
              data: JSON.stringify({
                data: {
                  ...parsedData,
                  cartItems: cartItems,
                },
              }),
            },
            "*",
          );
        } catch (error) {
          console.error("Error parsing cart data:", error);
          return;
        }
      }

      // Handle object messages
      if (typeof data === "object" && data !== null) {
        switch (data.action) {
          case "CART_SHEET_OPENED":
            const cartData =
              typeof data.data === "string" ? JSON.parse(data.data) : data.data;
            const initialTotalAmount = Array.isArray(cartData.cartItems)
              ? cartData.cartItems.reduce(
                  (total, item) =>
                    total + parseFloat(item.amount) * (item.quantity || 1),
                  0,
                )
              : 0;

            setCheckoutPayload({
              ...cartData,
              cartItems: Array.isArray(cartData.cartItems)
                ? cartData.cartItems
                : [],
            });
            setIsSheetOpen(true);
            break;

          case "UPDATED_CART":
            if (data.data) {
              try {
                const cartData =
                  typeof data.data === "string"
                    ? JSON.parse(data.data)
                    : data.data;

                setCheckoutPayload({
                  ...cartData,
                  cartItems: Array.isArray(cartData.cartItems)
                    ? cartData.cartItems
                    : [],
                });
              } catch (error) {
                console.error("Cart Update Error:", error);
              }
            }
            break;
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  useEffect(() => {
    //send message to parent to close the sheet when the isSheetOpen is false
    if (!isSheetOpen) {
      // wait for 100ms before sending the message
      setTimeout(() => {
        window.top.postMessage(
          {
            action: "CART_SHEET_CLOSED",
            entity: "CHARITYSTACK",
            sender: "ADD_TO_CART_SHEET",
          },
          "*",
        );
      }, 500);
    }
  }, [isSheetOpen]);

  // Handle sheet visibility changes
  useEffect(() => {
    const iframe = window.frameElement;
    if (iframe) {
      iframe.style.visibility = isSheetOpen ? "visible" : "hidden";
    }
  }, [isSheetOpen]);

  const handleRemoveItem = async (index, item) => {
    if (removingItems.has(index)) return;

    try {
      setRemovingItems((prev) => new Set([...prev, index]));

      // Send message to remove item
      window.top.postMessage(
        {
          action: "REMOVE_CART_ITEM",
          entity: "CHARITYSTACK",
          sender: "ADD_TO_CART_SHEET",
          id: elementID,
          cartItemKey: item.cartItemKey,
        },
        "*",
      );

      // Increased animation duration to match the transition
      await new Promise((resolve) => setTimeout(resolve, 300));

      setCheckoutPayload((prev) => {
        const newCartItems = prev.cartItems.filter((_, i) => i !== index);
        return {
          ...prev,
          cartItems: newCartItems,
        };
      });

      setRemovingItems((prev) => {
        const next = new Set(prev);
        next.delete(index);
        return next;
      });
    } catch (error) {
      console.error("Error removing item:", error);
      setRemovingItems((prev) => {
        const next = new Set(prev);
        next.delete(index);
        return next;
      });
    }
  };

  const handleQuantityChange = (index, item, increment) => {
    try {
      if (!item || typeof item !== "object") {
        console.error("Invalid item object:", item);
        return;
      }

      const newQuantity = (item.quantity || 1) + (increment ? 1 : -1);
      if (newQuantity < 1) return;

      setCheckoutPayload((prev) => {
        const updatedCartItems = prev.cartItems.map((cartItem, idx) =>
          idx === index ? { ...cartItem, quantity: newQuantity } : cartItem,
        );

        return {
          ...prev,
          cartItems: updatedCartItems,
        };
      });

      // Send update to backend
      window.top.postMessage(
        {
          action: "UPDATE_CART_QUANTITY",
          entity: "CHARITYSTACK",
          sender: "ADD_TO_CART_SHEET",
          id: elementID, // Important: Use sourceWebsite as ID
          data: JSON.stringify({
            cartItemKey: item.cartItemKey,
            quantity: newQuantity,
            productID: item.productID,
          }),
        },
        "*",
      );
    } catch (error) {
      console.error("Error updating quantity:", error);
      // Revert to backend state if there's an error
      window.top.postMessage(
        {
          action: "GET_CART",
          entity: "CHARITYSTACK",
          sender: "ADD_TO_CART_SHEET",
          id: elementID,
        },
        "*",
      );
    }
  };

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640); // 640px is Tailwind's 'sm' breakpoint
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Add this helper for date formatting
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "numeric",
      day: "numeric",
      year: "2-digit",
    });
  };

  const renderCartItem = (item, index) => (
    <div
      key={`${item.cartItemKey}-${index}`}
      className={`
        relative
        my-2
        flex
        flex-col
        items-start
        gap-2.5
        overflow-hidden
        rounded-md
        border
        border-[rgba(30,30,30,0.05)]
        bg-[#FCFEFF]
        shadow-[0px_1px_1px_rgba(0,0,0,0.25)]
        transition-all
        duration-300
        ease-in-out
        ${
          removingItems.has(index)
            ? "scale-98 my-0 max-h-0 translate-y-[-8px] py-0 opacity-0"
            : "max-h-[500px] translate-y-0 scale-100 opacity-100"
        }
      `}
    >
      <div className="flex w-full flex-row items-center justify-between bg-white p-3">
        <div className="flex min-w-0 flex-shrink flex-col gap-1">
          <div className="flex min-w-0 flex-col gap-0">
            <div className="open_sans truncate text-base font-normal leading-[1.48] tracking-wider text-[#1E1E1E]">
              {item.fundraiser}
            </div>
            <div className="open_sans h-full truncate text-base font-normal tracking-[0.5px] text-[#7E7E7E] [font-feature-settings:'liga'_off,'clig'_off]">
              {item.productGroupName
                ? `${item.productGroupName} · ${item.productName} · ${item.fundName}`
                : item.fundName}
            </div>
            {/* Date and Time Display */}
            {item.startDate && item.endDate && (
              <p className="open_sans truncate text-sm font-normal leading-[1.2] tracking-[0.5px] text-[#7E7E7E]">
                {formatDate(item.startDate)} - {formatDate(item.endDate)}
                {item.timeValue &&
                  ` • ${new Date(item.timeValue).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}`}
              </p>
            )}
          </div>
          {/* Quantity controls */}
          <div className="mt-1 flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              style={{
                backgroundColor:
                  (item.quantity || 1) <= 1 ? "#B3B3B3" : BUTTON_ACTIVE_COLOR,
              }}
              className="h-[18px] w-[18px] rounded-full p-0 transition-colors hover:brightness-90"
              onClick={() => handleQuantityChange(index, item, false)}
              disabled={(item.quantity || 1) <= 1}
            >
              <MinusIcon
                className={`h-3.5 w-3.5 ${
                  (item.quantity || 1) <= 1 ? "text-black" : "text-white"
                }`}
                style={{
                  shapeRendering: "geometricPrecision",
                  textRendering: "geometricPrecision",
                  strokeWidth: 2.5,
                }}
              />
            </Button>

            <span className="open_sans min-w-[20px] text-center text-base text-[#1E1E1E]">
              {item.quantity || 1}
            </span>

            <Button
              variant="ghost"
              size="icon"
              style={{ backgroundColor: BUTTON_ACTIVE_COLOR }}
              className="h-[18px] w-[18px] rounded-full p-0 transition-colors hover:brightness-90"
              onClick={() => handleQuantityChange(index, item, true)}
            >
              <PlusIcon
                className="h-3.5 w-3.5 text-white"
                style={{
                  shapeRendering: "geometricPrecision",
                  textRendering: "geometricPrecision",
                  strokeWidth: 2.5,
                }}
              />
            </Button>
          </div>
        </div>

        <div className="flex flex-shrink-0 flex-col items-end gap-1 pl-2">
          <div className="flex h-[52px] flex-col items-end justify-start">
            <span className="open_sans text-xl font-semibold tracking-[0.5px] text-[#1E1E1E] [font-feature-settings:'liga'_off,'clig'_off]">
              ${(item.amount || 0).toFixed(2)}
            </span>
            <span className="open_sans h-[24px] text-base font-normal tracking-[0.5px] text-[#7E7E7E] [font-feature-settings:'liga'_off,'clig'_off]">
              {freqMap[item.frequency]}
            </span>
          </div>

          <Button
            variant="custom"
            size="icon"
            // update this so that on click or on press it has bg transparent
            className="h-6 w-6 p-0 hover:bg-transparent "
            onClick={() => handleRemoveItem(index, item)}
          >
            <TrashIcon className="h-4 w-4 text-[#DB3737]" />
          </Button>
        </div>
      </div>
    </div>
  );

  const handleScroll = (event) => {
    const element = event.currentTarget;
    const isScrollable = element.scrollHeight > element.clientHeight;
    const isAtBottom =
      Math.abs(
        element.scrollHeight - element.clientHeight - element.scrollTop,
      ) < 1;

    // Only show gradient when scrollable AND not at bottom
    setShowGradient(isScrollable && !isAtBottom);
  };

  const renderCartContent = () => (
    <>
      <ScrollArea
        hideScrollbar
        className="relative flex-1"
        onScroll={handleScroll}
      >
        <div className="relative flex-1 pb-4">
          {checkoutPayload.cartItems?.length === 0 && (
            <div className="px-0">
              <p className="text-base text-gray-900 duration-500 ease-in-out animate-in fade-in">
                Your cart is empty.
              </p>
            </div>
          )}
          {checkoutPayload.cartItems?.map((item, index) =>
            renderCartItem(item, index),
          )}
        </div>
        {/* Gradient fade overlay - moved outside ScrollArea but inside the content wrapper */}
        <div
          className="pointer-events-none absolute bottom-0 left-0 right-0 h-14 bg-gradient-to-t from-[#F8FAFB] to-transparent transition-opacity duration-300"
          style={{
            opacity: showGradient ? 1 : 0,
          }}
        />
      </ScrollArea>

      {/* Rest of the content */}
      <hr className="h-[1px] w-full bg-[#D9D9D9]" />

      <div className="flex w-full flex-col items-start gap-8 rounded-md border border-[rgba(30,30,30,0.05)] bg-[#FCFEFF] p-8 shadow-[0px_1px_1px_rgba(0,0,0,0.25)]">
        <div className="flex w-full flex-col items-start gap-6">
          {/* Subtotal Section */}
          <div className="flex w-full flex-col gap-4">
            <div className="flex w-full flex-row items-center justify-between">
              <span className="open_sans text-base font-normal leading-[22px] tracking-[0.5px] text-[#1E1E1E]">
                Subtotal
              </span>
              <span className="open_sans text-base font-normal leading-[22px] tracking-[0.5px] text-[#1E1E1E]">
                $
                {(
                  checkoutPayload.cartItems.reduce(
                    (total, item) => total + item.amount * (item.quantity || 1),
                    0,
                  ) || 0.0
                ).toFixed(2)}
              </span>
            </div>

            <div className="h-[1px] w-full bg-[#D9D9D9]" />

            {/* Total Section */}
            <div className="flex w-full flex-row items-baseline justify-between">
              <span className="open_sans text-base font-normal leading-[22px] tracking-[0.5px] text-[#1E1E1E]">
                Total
              </span>
              <span className="open_sans text-2xl font-normal leading-[33px] tracking-[0.5px] text-[#1E1E1E]">
                $
                {(
                  checkoutPayload.cartItems.reduce(
                    (total, item) => total + item.amount * (item.quantity || 1),
                    0,
                  ) || 0.0
                ).toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex w-full flex-col gap-4">
        {checkoutPayload.cartItems.length > 0 && (
          <div className="duration-300 ease-in-out animate-in fade-in slide-in-from-top-2">
            <Button
              style={{
                backgroundColor: BUTTON_ACTIVE_COLOR,
                color: setContrast(BUTTON_ACTIVE_COLOR),
              }}
              className={`
            flex w-full items-center justify-center gap-2 self-stretch rounded-md 
            border p-[10px_20px] text-base text-white
          shadow-[0px_1px_1px_rgba(0,0,0,0.25)] transition-all transition-colors          
              duration-300 ease-in-out hover:brightness-90
              ${
                !checkoutPayload.cartItems.length
                  ? "animate-in fade-in slide-in-from-bottom-2"
                  : ""
              }
              `}
              onClick={() => {
                window.top.postMessage(
                  {
                    action: "DONATE",
                    entity: "CHARITYSTACK",
                    sender: "ADD_TO_CART_SHEET",
                    data: JSON.stringify(checkoutPayload),
                    donationData: JSON.stringify(checkoutPayload),
                  },
                  "*",
                );
                setIsSheetOpen(false);
              }}
            >
              Checkout
            </Button>
          </div>
        )}

        <Button
          variant="outline"
          className={`
            flex w-full items-center justify-center gap-2 self-stretch rounded-md 
            border p-[10px_20px] text-base text-gray-700 shadow-[0px_1px_1px_rgba(0,0,0,0.25)] 
            transition-all duration-300 ease-in-out hover:bg-[#F8FAFB]/90
            ${
              !checkoutPayload.cartItems.length
                ? "animate-in fade-in slide-in-from-bottom-2"
                : ""
            }
          `}
          onClick={() => setIsSheetOpen(false)}
          style={{
            color: "#1E1E1E",
            backgroundColor: brandColors["accent-10"],
            borderColor: "rgba(30, 30, 30, 0.15)",
          }}
        >
          Add other items
        </Button>
      </div>
    </>
  );

  return (
    <BrandColorProvider brandColors={brandColors}>
      <Sheet
        open={isSheetOpen}
        onOpenChange={(open) => {
          setIsSheetOpen(open);
          // if (!open) {
          //   window.top.postMessage(
          //     {
          //       action: "CART_SHEET_CLOSED",
          //       entity: "CHARITYSTACK",
          //       sender: "ADD_TO_CART_SHEET",
          //     },
          //     "*",
          //   );
          // }
        }}
        modal={true}
      >
        <SheetContent
          side={isMobile ? "bottom" : "right"}
          className="flex h-full w-full flex-col overflow-hidden bg-[#F8FAFB] sm:w-[475px]"
          onPointerDownOutside={(e) => {
            e.preventDefault();
            setIsSheetOpen(false);
          }}
          onEscapeKeyDown={() => setIsSheetOpen(false)}
        >
          <div className="flex items-center justify-between">
            <SheetTitle className="open_sans text-2xl font-normal text-black">
              Your Cart
            </SheetTitle>
            <button
              onClick={() => setIsSheetOpen(false)}
              className="flex cursor-pointer items-center rounded-full bg-black/25 p-1 transition-opacity"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                height="24"
                viewBox="0 0 24 24"
                width="24"
                className="fill-white"
                style={{
                  filter: "drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.25))",
                }}
              >
                <path d="M0 0h24v24H0z" fill="none" />
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </button>
          </div>

          {renderCartContent()}
        </SheetContent>
        <Tooltip id="remove-tooltip" />
      </Sheet>
    </BrandColorProvider>
  );
};

export default AddToCartSheet;
